import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAccountQuery } from "../../../../api/features/account/use-account.query";
import {
  EMAIL_VERIFICATION_SENT_PATH,
  EXTERNAL_LOGIN_PATH,
  MIGRATE_PROFILE_PAGE_PATH,
  VERIFY_EMAIL_PATH,
} from "../../../../constants/env";
import { useIsLogged } from "../../../../hooks/auth/use-is-logged/use-is-logged";
import BonusXLoading from "../../../../legacy/components/BonusXLoading";
import { useProfileMigrationQuery } from "../../../../api/features/profile/useProfileMigration.query";
import { DEFAULT_CONTEXT_ID, ONBOARDING_SURVEY_PATH } from "../../../../legacy/pages/survey/config/survey-paths";

const NOT_VERIFIED_ALLOWED_PATHS = [EMAIL_VERIFICATION_SENT_PATH, "/logout"];
const MIGRATION_ALLOWED_PATHS = [MIGRATE_PROFILE_PAGE_PATH, "/logout"];

export function AppStateGuard(): JSX.Element {
  const isLogged = useIsLogged();
  const { accountData: account, loading: accountLoading } = useAccountQuery();
  const { profileMigration, loading: profileMigrationLoading } = useProfileMigrationQuery();
  const location = useLocation();
  const isExternalLoginPath = location.pathname === EXTERNAL_LOGIN_PATH;
  if (isLogged && accountLoading && !isExternalLoginPath) {
    return <BonusXLoading />;
  }

  const isAccountNotVerified = account?.verified === false;
  if (isAccountNotVerified) {
    if (!NOT_VERIFIED_ALLOWED_PATHS.includes(location.pathname)) {
      return <Navigate to={EMAIL_VERIFICATION_SENT_PATH} replace />;
    }
    return <Outlet />;
  }

  const isAccountVerified = account?.verified === true;
  const isAccountProfileUncompleted = account?.profileCompleted === false;

  if (isAccountVerified && isAccountProfileUncompleted) {
    const isSurveyPath = location.pathname.startsWith("/survey/");
    if (!isSurveyPath && location.pathname !== VERIFY_EMAIL_PATH) {
      return <Navigate to={ONBOARDING_SURVEY_PATH.replace(":contextId", DEFAULT_CONTEXT_ID)} replace />;
    }

    return <Outlet />;
  }

  if (isLogged && profileMigrationLoading && !isExternalLoginPath) {
    return <BonusXLoading />;
  }

  if (profileMigration?.isMigrationInProgress && !isExternalLoginPath) {
    if (!MIGRATION_ALLOWED_PATHS.includes(location.pathname)) {
      return <Navigate to={MIGRATE_PROFILE_PAGE_PATH} replace />;
    }
    return <Outlet />;
  }

  return <Outlet />;
}
